import * as React from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import Config from "../config.json";


// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2e2e2e",
    color: "#ffffff",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "rgba(46, 46, 46, 0.04)",
  },
}));

// Custom hook for announcement data
function useAnnouncement(url) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!url) return;
    fetch(url)
      .then((res) => res.json())
      .then((res) => setData(res.data || []))
      .catch((err) => console.error("Fetch error:", err));
  }, [url]);

  return data;
}

// Project type configurations
const projectConfigs = {
  urban: {
    label: "Urban Base Maps",
    path: "urban",
    color: "#1976d2",
    bgColor: "rgba(25, 118, 210, 0.1)"
  },
  nonurban: {
    label: "Non-Urban Base Maps", 
    path: "nonurban",
    color: "#388e3c",
    bgColor: "rgba(56, 142, 60, 0.1)"
  },
  qcqa: {
    label: "QC/QA Base Maps",
    path: "qc", 
    color: "#f57c00",
    bgColor: "rgba(245, 124, 0, 0.1)"
  },
  pmc: {
    label: "PMC",
    path: "pmc",
    color: "#7b1fa2",
    bgColor: "rgba(123, 31, 162, 0.1)"
  },
  generalisasi: {
    label: "Generalisasi",
    path: "generalisasi",
    color: "#00796b",
    bgColor: "rgba(0, 121, 107, 0.1)"
  },
  cons: {
    label: "Procurement Specialist (5.2)",
    path: "cps",
    color: "#d32f2f",
    bgColor: "rgba(211, 47, 47, 0.1)"
  },
  climate: {
    label: "Climate Change Policy Expert",
    path: "Climate-Change",
    color: "#2e7d32",
    bgColor: "rgba(46, 125, 50, 0.1)"
  },
  perpus: {
    label: "Koleksi Perpustakaan",
    path: "penambahan-koleksi-perpustakaan", 
    color: "#795548",
    bgColor: "rgba(121, 85, 72, 0.1)"
  },
  perpus1: {
    label: "Koleksi Perpustakaan (ULANG)",
    path: "penambahan-koleksi-perpustakaan", 
    color: "#795548",
    bgColor: "rgba(121, 85, 72, 0.1)"
  },
  geo: {
    label: "Geospatial Data Expert",
    path: "GeoDataUtilization",
    color: "#1565c0",
    bgColor: "rgba(21, 101, 192, 0.1)"
  },
  pengadaan: {
    label: "Procurement Specialist",
    path: "procurement-specialist",
    color: "#e65100",
    bgColor: "rgba(230, 81, 0, 0.1)"
  },
  risiko: {
    label: "Risk Management",
    path: "risk-management",
    color: "#c62828",
    bgColor: "rgba(198, 40, 40, 0.1)"
  },
  proyek: {
    label: "Project Management",
    path: "project-management",
    color: "#6a1b9a",
    bgColor: "rgba(106, 27, 154, 0.1)"
  },
  enhancement: {
    label: "Geospatial Capacity Enhancement",
    path: "capacity-enhancement",
    color: "#4527a0",
    bgColor: "rgba(69, 39, 160, 0.1)"
  },
  multimedia: {
    label: "PC, Laptop & Multimedia",
    path: "pengadaan-multimedia",
    color: "#ff6f00",
    bgColor: "rgba(255, 111, 0, 0.1)"
  },
  sarana: {
    label: "Peningkatan Sarana",
    path: "peningkatan-sarana",
    color: "#bf360c",
    bgColor: "rgba(191, 54, 12, 0.1)"
  },
  multimedia1: {
    label: "PC, Laptop & Multimedia (ULANG)",
    path: "pengadaan-multimedia",
    color: "#ff6f00",
    bgColor: "rgba(255, 111, 0, 0.1)"
  },
  sarana1: {
    label: "Peningkatan Sarana (ULANG)",
    path: "peningkatan-sarana",
    color: "#bf360c",
    bgColor: "rgba(191, 54, 12, 0.1)"
  },
  sarana2: {
    label: "Peningkatan Sarana (ULANG 2)",
    path: "peningkatan-sarana",
    color: "#bf360c",
    bgColor: "rgba(191, 54, 12, 0.1)"
  },
  geoclimate: {
    label: "Geospatial Climate Platform",
    path: "geospatial-platform-for-climate-action",
    color: "#1b5e20",
    bgColor: "rgba(27, 94, 32, 0.1)"
  },
  devimaps: {
    label: "Integrated Map Production",
    path: "development-of-integrated-map-production-system",
    color: "#283593",
    bgColor: "rgba(40, 53, 147, 0.1)"
  }
};

// Unified Announcement Table
const UnifiedAnnouncementTable = ({ allData, selectedFilter }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const handleChangePage = (_, newPage) => setPage(newPage);

  // Filter data based on selected project
  const filteredData = selectedFilter === "all" 
    ? allData 
    : allData.filter(item => item.projectType === selectedFilter);

  // Sort filtered data by time_uploaded (newest first)
  const sortedData = [...filteredData].sort(
    (a, b) => new Date(b.time_uploaded) - new Date(a.time_uploaded)
  );

  const slicedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [selectedFilter]);

  const baseUrl = "https://ilaspp-proc.big.go.id/#/";

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFilteredProjectCount = () => {
    if (selectedFilter === "all") {
      const uniqueProjects = [...new Set(allData.map(item => item.projectType))];
      return uniqueProjects.length;
    }
    return 1;
  };

  return (
    <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Project Name</StyledTableCell>
              <StyledTableCell>Announcement</StyledTableCell>
              <StyledTableCell align="center">Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slicedData.length > 0 ? (
              slicedData.map((row) => {
                const config = projectConfigs[row.projectType];
                return (
                  <StyledTableRow key={`${row.projectType}-${row.public_id}`}>
                    <StyledTableCell sx={{ width: '220px' }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          backgroundColor: config.bgColor,
                          color: config.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: `1px solid ${config.color}`,
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <a
                        href={`${baseUrl}${config.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: config.color, 
                          textDecoration: "none",
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = 'underline';
                          e.target.style.opacity = '0.8';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = 'none';
                          e.target.style.opacity = '1';
                        }}
                      >
                        {row.title}
                      </a>
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: '120px' }}>
                      <Typography variant="caption" sx={{ 
                        color: '#666',
                        fontWeight: 500
                      }}>
                        {formatDate(row.time_uploaded)}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <StyledTableRow>
                <StyledTableCell align="center" colSpan={3}>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontStyle: 'italic',
                    padding: '20px'
                  }}>
                    {selectedFilter === "all" ? "No Announcements Found" : selectedFilter === "qcqa" ? "The work package has not started yet. Project activities and progress updates will be provided once implementation begins." : `No Announcements Found for ${projectConfigs[selectedFilter]?.label}`}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: '16px',
            paddingRight: '8px',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            color: '#666'
          }
        }}
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} of ${count} announcements ${selectedFilter !== "all" ? `from ${projectConfigs[selectedFilter]?.label}` : `from ${getFilteredProjectCount()} projects`}`
        }
      />
    </Paper>
  );
};

// Main Component
export default function DashboardPage() {
  // Filter state
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [openDialog, setOpenDialog] = React.useState(false);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  // All API endpoints for announcements
  const announcementUrban = useAnnouncement(Config.api_domain + "/pengumuman/active/");
  const announcementNonUrban = useAnnouncement(Config.api_domain + "/berkas/active/");
  const announcementQCQA = useAnnouncement(Config.api_domain + "/qcqa/active/");
  const announcementPMC = useAnnouncement(Config.api_domain + "/pmc/active/");
  const announcementGeneralisasi = useAnnouncement(Config.api_domain + "/generalisasi/active/");
  const announcementCONS = useAnnouncement(Config.api_domain + "/cons/active/");
  const announcementClimate = useAnnouncement(Config.api_domain + "/climate/active/");
  const announcementGeo = useAnnouncement(Config.api_domain + "/geo/active/");
  const announcementPengadaan = useAnnouncement(Config.api_domain + "/pengadaan/active/");
  const announcementRisiko = useAnnouncement(Config.api_domain + "/risiko/active/");
  const announcementProyek = useAnnouncement(Config.api_domain + "/proyek/active/");
  const announcementEnhancement = useAnnouncement(Config.api_domain + "/enhancement/active/");
  const announcementMultimedia = useAnnouncement(Config.api_domain + "/multimedia/active/");
  const announcementSarana = useAnnouncement(Config.api_domain + "/sarana/active/");
  const announcementMultimedia1 = useAnnouncement(Config.api_domain + "/multimedia1/active/");
  const announcementPerpus = useAnnouncement(Config.api_domain + "/perpus/active/");
  const announcementPerpus1 = useAnnouncement(Config.api_domain + "/perpus1/active/");
  const announcementSarana1 = useAnnouncement(Config.api_domain + "/sarana1/active/");
  const announcementSarana2 = useAnnouncement(Config.api_domain + "/sarana2/active/");
  const announcementGeoClimate = useAnnouncement(Config.api_domain + "/geoclimate/active/");
  const announcementDevimaps = useAnnouncement(Config.api_domain + "/devimaps/active/");

  // Combine all announcements with project type labels
  const getAllAnnouncementsData = () => {
    const combinedData = [
      ...announcementUrban.map((d) => ({ 
        ...d, 
        projectType: "urban",
        download_path: "pengumuman/download" 
      })),
      ...announcementNonUrban.map((d) => ({ 
        ...d, 
        projectType: "nonurban",
        download_path: "berkas/download" 
      })),
      ...announcementQCQA.map((d) => ({ 
        ...d, 
        projectType: "qcqa",
        download_path: "qcqa/download" 
      })),
      ...announcementPMC.map((d) => ({ 
        ...d, 
        projectType: "pmc",
        download_path: "pmc/download" 
      })),
      ...announcementPerpus.map((d) => ({ 
        ...d, 
        projectType: "perpus",
        download_path: "penambahan-koleksi-perpustakaan/download" 
      })),
      ...announcementPerpus1.map((d) => ({ 
        ...d, 
        projectType: "perpus1",
        download_path: "penambahan-koleksi-perpustakaan/download" 
      })),
      ...announcementGeneralisasi.map((d) => ({ 
        ...d, 
        projectType: "generalisasi",
        download_path: "generalisasi/download" 
      })),
      ...announcementCONS.map((d) => ({ 
        ...d, 
        projectType: "cons",
        download_path: "cons/download" 
      })),
      ...announcementClimate.map((d) => ({ 
        ...d, 
        projectType: "climate",
        download_path: "climate/download" 
      })),
      ...announcementGeo.map((d) => ({ 
        ...d, 
        projectType: "geo",
        download_path: "geo/download" 
      })),
      ...announcementPengadaan.map((d) => ({ 
        ...d, 
        projectType: "pengadaan",
        download_path: "pengadaan/download" 
      })),
      ...announcementRisiko.map((d) => ({ 
        ...d, 
        projectType: "risiko",
        download_path: "risiko/download" 
      })),
      ...announcementProyek.map((d) => ({ 
        ...d, 
        projectType: "proyek",
        download_path: "proyek/download" 
      })),
      ...announcementEnhancement.map((d) => ({ 
        ...d, 
        projectType: "enhancement",
        download_path: "enhancement/download" 
      })),
      ...announcementMultimedia.map((d) => ({ 
        ...d, 
        projectType: "multimedia",
        download_path: "multimedia/download" 
      })),
      ...announcementSarana.map((d) => ({ 
        ...d, 
        projectType: "sarana",
        download_path: "sarana/download" 
      })),
      ...announcementMultimedia1.map((d) => ({ 
        ...d, 
        projectType: "multimedia1",
        download_path: "multimedia/download" 
      })),
      ...announcementSarana1.map((d) => ({ 
        ...d, 
        projectType: "sarana1",
        download_path: "sarana1/download" 
      })),
      ...announcementSarana2.map((d) => ({ 
        ...d, 
        projectType: "sarana2",
        download_path: "sarana2/download" 
      })),
      ...announcementGeoClimate.map((d) => ({ 
        ...d, 
        projectType: "geoclimate",
        download_path: "geoclimate/download" 
      })),
      ...announcementDevimaps.map((d) => ({ 
        ...d, 
        projectType: "devimaps",
        download_path: "devimaps/download" 
      })),
    ];
    
    return combinedData;
  };

  const allAnnouncementsData = getAllAnnouncementsData();

  return (
    <Box padding={2}>
      <Box display="flex" width="100%" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        {/* Left side: Description */}
        <Box flex={{ xs: "1", md: "0 0 60%" }}>
          <Typography
            variant="h3"
            component="h3"
            gutterBottom
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              mb: 2,
              color: "#2e2e2e",
            }}
          >
            ILASPP PROCUREMENT
          </Typography>

          {/* Paragraf kiri (tidak justify lagi) */}
          <Box sx={{ width: "95%" }}>
            <Typography
              variant="body1"
              paragraph
              align="left"
              sx={{
                lineHeight: 1.6,
                color: "#333",
              }}
            >
              To support the implementation of ILASPP objectives, ILASPP Procurement has been
              established to accommodate the production of large-scale geospatial data. This system
              ensures transparency, competitiveness, and efficiency in the tendering process for
              mapping services. It is designed to inform qualified vendors with the technical
              capacity to deliver high-resolution base maps at multiple scales—ranging from 1:5,000
              to 1:1,000,000—covering approximately 82,497 square kilometers. The procurement
              mechanism aligns with international standards and is structured to facilitate timely
              execution from 2025 to 2027, while ensuring quality and compliance with
              climate-resilient spatial planning objectives.
            </Typography>
          </Box>
          {/* === Tabel di bawah paragraf === */}
          <Typography
            variant="h6"
            sx={{ mt: 3, mb: 1, fontWeight: "bold", color: "#2e2e2e" }}
          >
            BID OPENING ANNOUNCEMENT
          </Typography>

          <Paper sx={{ borderRadius: "12px", maxWidth: "95%", overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: '60px' }}>No</StyledTableCell>
                    <StyledTableCell>Announcement</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: '140px' }}>Event Date</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: '120px' }}>Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip
                        label="1"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          color: '#1976d2',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: '1px solid #1976d2',
                          minWidth: '32px'
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                        Technical Parts Bid Opening for The Provision of Basic Geospatial Data And Base
                        Maps of Urban and Non-Urban Areas Of Indonesia
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ 
                        color: '#666',
                        fontWeight: 500
                      }}>
                        21 October 2025, 13:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleOpenDialog}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#1565c0',
                          },
                          boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                        }}
                      >
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* === Dialog Popup === */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Technical Parts Bid Opening - Zoom Meeting</DialogTitle>
            <DialogContent dividers>
              <DialogContentText
  sx={{
    whiteSpace: "pre-line",
    fontSize: "0.9rem",
    color: "#333",
  }}
>
  Procurement Working Group ILASPP DPRWD is inviting you to a scheduled Zoom meeting.

  {"\n\n"}<strong>Topic:</strong> Technical Parts Bid Opening for The Provision of Basic Geospatial Data And Base Maps of Urban and Non-Urban Areas Of Indonesia

  {"\n\n"}<strong>Time:</strong> Oct 21, 2025 01:00 PM Jakarta

  {"\n\n"}<strong>Join Zoom Meeting:</strong>{" "}
  <a
    href="https://big-go-id.zoom.us/j/95954655851?pwd=wDEdZgpixORb8u8884SOR3yJEPPuZV.1"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#1976d2", textDecoration: "underline" }}
  >
    https://big-go-id.zoom.us/j/95954655851?pwd=wDEdZgpixORb8u8884SOR3yJEPPuZV.1
  </a>

  {"\n\n"}<strong>Meeting ID:</strong> 959 5465 5851  
  {"\n"}<strong>Passcode:</strong> M6=ecW%S

  {"\n\n"}<strong>Join instructions:</strong>{" "}
  <a
    href="https://big-go-id.zoom.us/meetings/95954655851/invitations?signature=FE2pEw7EfBVxvneylhS6D-8Jpe-STaeAl1oGt_OWut4"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#1976d2", textDecoration: "underline" }}
  >
    https://big-go-id.zoom.us/meetings/95954655851/invitations?signature=FE2pEw7EfBVxvneylhS6D-8Jpe-STaeAl1oGt_OWut4
  </a>
</DialogContentText>

            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Right side: Filter + Unified Announcement Table */}
        <Box flex={{ xs: "1", md: "0 0 40%" }}>
          <Typography variant="h3" component="h3" gutterBottom sx={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            mb: 2,
            color: '#2e2e2e'
          }}>
            LATEST ANNOUNCEMENTS
          </Typography>

          {/* Project Filter Dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="project-filter-label" sx={{ fontSize: '0.875rem' }}>
              Filter by Project
            </InputLabel>
            <Select
              labelId="project-filter-label"
              value={selectedFilter}
              label="Filter by Project"
              onChange={(e) => setSelectedFilter(e.target.value)}
              sx={{
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  padding: '12px 14px',
                }
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label="ALL" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(46, 46, 46, 0.1)',
                      color: '#2e2e2e',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }} 
                  />
                  All Projects
                </Box>
              </MenuItem>
              {Object.entries(projectConfigs).map(([key, config]) => (
                <MenuItem key={key} value={key} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={key.toUpperCase()} 
                      size="small" 
                      sx={{ 
                        backgroundColor: config.bgColor,
                        color: config.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: `1px solid ${config.color}`,
                        minWidth: '45px'
                      }} 
                    />
                    {config.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ 
            mb: 2, 
            color: '#666',
            fontStyle: 'italic'
          }}>
            {selectedFilter === "all" 
              ? "Showing latest announcements from all 16 projects" 
              : `Showing announcements from ${projectConfigs[selectedFilter]?.label}`
            }
          </Typography>

          {/* Unified Announcement Table */}
          <UnifiedAnnouncementTable 
            allData={allAnnouncementsData} 
            selectedFilter={selectedFilter}
          />
        </Box>
      </Box>
    </Box>
  );
}